import StaticPageLayout from "@/components/layout/StaticPageLayout";
import { useLanguage } from "@/context/LanguageContext";

export default function SafetyTips() {
  const { language } = useLanguage();

  const contentEn = (
    <div className="space-y-6 text-sm text-muted-foreground leading-relaxed">
      <p>
        At Hamarsathi, safety is paramount. While we deploy ID checking, photo verification, and community moderation, users must take active precautions. Below is a detailed guide on how to protect yourself while searching for a life partner online.
      </p>

      <section className="space-y-3">
        <h2 className="text-xl font-bold text-[#2A0A10]">1. Keeping Initial Conversations on Hamarsathi</h2>
        <p>
          Always communicate using our built-in secure Chat feature during the initial phase. Avoid sharing your personal phone number, email address, home address, workplace, or social media links until you have established basic trust with the other member. Our platform's chat feature keeps your identity protected.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-bold text-[#2A0A10]">2. Protecting Your Financial Information</h2>
        <p>
          <strong>Never send money, transfer funds, or share bank details under any circumstances.</strong> Matrimonial scammers often try to gain sympathy by asking for immediate financial help, citing reasons like:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Sudden medical emergency or accident in their family.</li>
          <li>Help to pay customs fees for a gift sent from abroad.</li>
          <li>Temporary cash shortage while traveling.</li>
        </ul>
        <p>
          If a user asks you for money or financial support, block them immediately and report them to our Grievance Officer at <a href="mailto:grievances@hamarsathi.com" className="text-primary underline">grievances@hamarsathi.com</a>.
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-bold text-[#2A0A10]">3. Guide to First In-Person Meetings</h2>
        <p>
          When you feel ready to meet the candidate in person, always follow these rules to ensure your safety:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Choose a Public Venue:</strong> Meet in a crowded, well-lit, public location (such as a popular cafe, restaurant, or shopping mall). Never meet in private residences, hotel rooms, or secluded areas.</li>
          <li><strong>Inform Family/Friends:</strong> Tell a family member or close friend where you are going, who you are meeting, and what time you expect to return. Share their phone number or profile details with them.</li>
          <li><strong>Arrange Your Own Transport:</strong> Do not rely on the other person to pick you up or drop you off. Arrange your own transport to and from the venue.</li>
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-bold text-[#2A0A10]">4. Red Flags to Watch Out For</h2>
        <p>Be alert if the other user displays any of the following behaviors:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Refuses to speak on video call or meet in person after multiple requests.</li>
          <li>Pressure to make decisions or finalize alliances too quickly.</li>
          <li>Inconsistent statements about their education, job, income, or family.</li>
          <li>Asks suspicious personal questions early in the conversation (bank details, passwords, property).</li>
        </ul>
      </section>
    </div>
  );

  const contentHi = (
    <div className="space-y-6 text-sm text-muted-foreground leading-relaxed">
      <p>
        हमरसाथी में, आपकी सुरक्षा हमारी सर्वोच्च प्राथमिकता है। यद्यपि हम प्रोफाइल की कड़ी जांच, फोटो सत्यापन और मॉडरेशन लागू करते हैं, फिर भी उपयोगकर्ताओं को स्वयं भी सतर्क रहना चाहिए। जीवनसाथी की खोज के दौरान खुद को सुरक्षित रखने के लिए नीचे दिए गए दिशानिर्देशों का पालन करें।
      </p>

      <section className="space-y-3">
        <h2 className="text-xl font-bold text-[#2A0A10]">1. शुरुआती बातचीत को हमरसाथी पर ही रखें</h2>
        <p>
          शुरुआती चरण के दौरान हमेशा हमारी इन-बिल्ट चैट सुविधा का उपयोग करके बातचीत करें। जब तक आप दूसरे सदस्य के साथ बुनियादी विश्वास स्थापित नहीं कर लेते, तब तक अपना व्यक्तिगत फोन नंबर, ईमेल पता, घर का पता, कार्यालय का स्थान या सोशल मीडिया लिंक साझा न करें।
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-bold text-[#2A0A10]">2. अपनी वित्तीय जानकारी की सुरक्षा</h2>
        <p>
          <strong>किसी भी परिस्थिति में कभी भी पैसे न भेजें, फंड ट्रांसफर न करें या बैंक विवरण साझा न करें।</strong> वैवाहिक जालसाज अक्सर भावनात्मक रूप से प्रभावित करके पैसे मांगते हैं, जैसे:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>परिवार में अचानक कोई चिकित्सा आपातकाल (मेडिकल इमरजेंसी) या दुर्घटना।</li>
          <li>विदेश से भेजे गए किसी उपहार के लिए सीमा शुल्क (कस्टम ड्यूटी) का भुगतान करना।</li>
          <li>यात्रा के दौरान अचानक नकदी की कमी होना।</li>
        </ul>
        <p>
          यदि कोई उपयोगकर्ता आपसे पैसे या वित्तीय सहायता मांगता है, तो उसे तुरंत ब्लॉक करें और हमारे शिकायत अधिकारी को <a href="mailto:grievances@hamarsathi.com" className="text-primary underline">grievances@hamarsathi.com</a> पर रिपोर्ट करें।
        </p>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-bold text-[#2A0A10]">3. पहली मुलाकात के नियम</h2>
        <p>
          जब आप व्यक्तिगत रूप से मिलने के लिए तैयार महसूस करें, तो अपनी सुरक्षा सुनिश्चित करने के लिए हमेशा इन नियमों का पालन करें:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>सार्वजनिक स्थान चुनें:</strong> किसी भीड़भाड़ वाले, रोशनी से युक्त, सार्वजनिक स्थान (जैसे लोकप्रिय कैफे, रेस्तरां या शॉपिंग मॉल) पर मिलें। कभी भी निजी घरों, होटल के कमरों या सुनसान इलाकों में न मिलें।</li>
          <li><strong>परिवार/मित्रों को सूचित करें:</strong> अपने परिवार के किसी सदस्य या करीबी मित्र को बताएं कि आप कहां जा रहे हैं, किससे मिल रहे हैं और कब लौटने की उम्मीद है। उनका फोन नंबर या प्रोफाइल विवरण उनके साथ साझा करें।</li>
          <li><strong>परिवहन की स्वयं व्यवस्था करें:</strong> आने-जाने के लिए दूसरे व्यक्ति के वाहन पर निर्भर न रहें। कार्यक्रम स्थल तक स्वयं के परिवहन की व्यवस्था करें।</li>
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-xl font-bold text-[#2A0A10]">4. खतरे के संकेत (Red Flags)</h2>
        <p>यदि दूसरा उपयोगकर्ता निम्नलिखित में से कोई भी व्यवहार दिखाता है तो सतर्क हो जाएं:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>बार-बार अनुरोध करने के बाद भी वीडियो कॉल पर बात करने या व्यक्तिगत रूप से मिलने से मना करना।</li>
          <li>बहुत जल्दी निर्णय लेने या शादी पक्की करने के लिए दबाव बनाना।</li>
          <li>अपनी शिक्षा, नौकरी, आय या परिवार के बारे में असंगत या विरोधाभासी बातें करना।</li>
          <li>बातचीत में बहुत जल्दी व्यक्तिगत सवाल पूछना (बैंक विवरण, पासवर्ड, संपत्ति)।</li>
        </ul>
      </section>
    </div>
  );

  return (
    <StaticPageLayout
      title={language === "hi" ? "सुरक्षा युक्तियाँ" : "Safety Tips"}
      subtitle={language === "hi" ? "एक सुरक्षित और खुशहाल जीवनसाथी खोज के लिए दिशानिर्देश" : "Guidelines for a safe and secure partner search"}
    >
      {language === "hi" ? contentHi : contentEn}
    </StaticPageLayout>
  );
}
